import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, Download, CalendarClock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import orchardService from '../../services/orchard';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { cn } from '../../utils/cn';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';
import type { NurseryCustomer } from '../../types/nursery';
import type { RangeExclusion, RetailerRange as Range } from '../../types/nurseryRetail';

/**
 * /nursery/retailer-range — the file that tells the Bunnings app which of our
 * trees stores can order now. Ported from the legacy "Vic Bunnings App
 * Upload" and "S.A. and N.T. Bunnings App Upload" pages.
 *
 * A tree is on it when it is priced on the head office's own contract list,
 * has a barcode, its tree type says a pot size, and enough are free and ready
 * at the farm (Rob, 2026-10-05). The page shows every priced tree and why any
 * is left out; the file is downloaded here and uploaded to Bunnings by hand.
 *
 * docs/retailer-po-and-cross-dock-plan.md → "Range upload"
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';
const PREFS_KEY = 'nursery_retailer_range_prefs';

const REASONS: Record<RangeExclusion, string> = {
  inactive: 'No longer sold',
  no_barcode: 'No barcode',
  bad_barcode: 'Barcode is not 13 digits',
  no_pot_size: 'Tree type gives no pot size',
  below_minimum: 'Not enough free',
  duplicate_barcode: 'Barcode already listed',
};

interface Prefs { customer_id?: number; farm_id?: number; min_free?: number; supplier_range?: string }

function readPrefs(): Prefs {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') as Prefs;
  } catch {
    return {};
  }
}

function savePrefs(p: Prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // A private window: the choices are simply not remembered.
  }
}

function RetailerRange() {
  const navigate = useNavigate();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [offices, setOffices] = useState<NurseryCustomer[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [minFree, setMinFree] = useState('20');
  const [supplierRange, setSupplierRange] = useState('Fruit Tree');
  const [range, setRange] = useState<Range | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Head offices and farms, and what was chosen last time.
  useEffect(() => {
    if (!canManage) return;
    const prefs = readPrefs();
    if (prefs.min_free != null) setMinFree(String(prefs.min_free));
    if (prefs.supplier_range) setSupplierRange(prefs.supplier_range);
    let cancelled = false;
    (async () => {
      try {
        const [custRes, farmRes] = await Promise.all([
          nurseryService.getCustomers({ top_level: true, active: true }),
          orchardService.getFarms(1, 100),
        ]);
        if (cancelled) return;
        const list = custRes.success ? custRes.data || [] : [];
        // Accounts with stores: a head office.
        const heads = list.filter((c) => (c.site_count ?? 0) > 0);
        const choices = heads.length ? heads : list;
        setOffices(choices);
        const farmList = farmRes.success ? (farmRes.data?.farms || []).map((f) => ({ id: f.id, name: f.name })) : [];
        setFarms(farmList);
        const keepCustomer = choices.find((c) => c.id === prefs.customer_id)
          ?? choices.find((c) => /bunnings/i.test(c.name)) ?? choices[0];
        setCustomerId(keepCustomer?.id ?? null);
        // The farm last used, else the head office's own despatch farm, else the only one.
        const keepFarm = farmList.find((f) => f.id === prefs.farm_id)
          ?? farmList.find((f) => f.id === keepCustomer?.despatch_farm_id)
          ?? (farmList.length === 1 ? farmList[0] : undefined);
        setFarmId(keepFarm?.id ?? null);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load the customers and farms'));
      }
    })();
    return () => { cancelled = true; };
  }, [canManage]);

  const minFreeNum = Number.parseInt(minFree, 10);
  const query = useMemo(
    () => (customerId && farmId && Number.isFinite(minFreeNum) && minFreeNum >= 0
      ? { customer_id: customerId, farm_id: farmId, min_free: minFreeNum, supplier_range: supplierRange.trim() || undefined }
      : null),
    [customerId, farmId, minFreeNum, supplierRange],
  );

  // The range for what is chosen, read again when a choice changes.
  useEffect(() => {
    if (!query) {
      setRange(null);
      return;
    }
    savePrefs({ customer_id: query.customer_id, farm_id: query.farm_id, min_free: query.min_free, supplier_range: query.supplier_range });
    let cancelled = false;
    setLoading(true);
    setError(null);
    nurseryRetailService.getRange(query)
      .then((res) => {
        if (cancelled) return;
        if (res.success) setRange(res.data ?? null);
        else setError(errorMessage(res, 'Could not work out the range'));
      })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, 'Could not work out the range')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const download = async () => {
    if (!query) return;
    setDownloading(true);
    setError(null);
    try {
      const blob = await nurseryRetailService.getRangeFile(query);
      const farmName = farms.find((f) => f.id === query.farm_id)?.name ?? 'farm';
      downloadBlob(blob, `Bunnings-app-Upload-${farmName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '')}.csv`);
    } catch (e) {
      setError(errorMessage(e, 'Could not make the file'));
    } finally {
      setDownloading(false);
    }
  };

  if (licenseLoading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what="The range upload is made" />;

  const included = range?.rows.filter((r) => r.included) ?? [];
  const excluded = range?.rows.filter((r) => !r.included) ?? [];

  return (
    <>
      <PageHeader
        title="Bunnings range upload"
        subtitle="Which of our trees stores can order now, as the file the Bunnings app takes."
        icon={<ListChecks className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/cross-dock')}>
              <CalendarClock className="h-4 w-4" /> Cross dock schedules
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-neutral-600">
            A tree goes in the file when it is priced on the head office&rsquo;s own price list, has a barcode, its tree type
            gives a pot size, and at least the minimum are ready and free at the farm.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="range-customer" className="block text-sm font-medium text-neutral-700 mb-1">Head office</label>
              <select id="range-customer" className={inputCls} value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose…</option>
                {offices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="range-farm" className="block text-sm font-medium text-neutral-700 mb-1">Ready at</label>
              <select id="range-farm" className={inputCls} value={farmId ?? ''}
                onChange={(e) => setFarmId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose…</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="range-min" className="block text-sm font-medium text-neutral-700 mb-1">At least this many free</label>
              <input id="range-min" className={inputCls} type="text" inputMode="numeric" value={minFree}
                onChange={(e) => setMinFree(e.target.value.replace(/\D/g, ''))} />
            </div>
            <div>
              <label htmlFor="range-supplier" className="block text-sm font-medium text-neutral-700 mb-1">Supplier range column</label>
              <input id="range-supplier" className={inputCls} maxLength={120} value={supplierRange}
                onChange={(e) => setSupplierRange(e.target.value)} />
            </div>
          </div>
        </section>

        {loading && !range && <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />}

        {range && (
          <>
            {range.price_lists.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 break-words">
                {range.customer.name} has no price list of its own in force today, so nothing is on its range. Someone with an
                Accounts seat gives it a contract price list.
              </div>
            ) : (
              <p className="text-sm text-neutral-600 break-words">
                From {range.price_lists.map((l) => l.name).join(', ')} · free and ready at {range.farm.name}
              </p>
            )}

            <section aria-label="In the file" className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-neutral-800">
                  {included.length} tree{included.length === 1 ? '' : 's'} in the file
                  {excluded.length > 0 && <span className="text-neutral-500"> · {excluded.length} left out</span>}
                </h2>
                <Button size="sm" onClick={download} disabled={downloading || included.length === 0}>
                  <Download className="h-4 w-4" /> {downloading ? 'Making…' : 'Download the file'}
                </Button>
              </div>
              {included.length > 0 && (
                <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
                  {included.map((r) => (
                    <li key={`${r.kind}-${r.id}`} className="px-4 py-2 flex items-start gap-3 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-neutral-900 break-words">{r.description}</span>
                        <span className="block text-xs text-neutral-500 break-words">
                          {r.barcode} · {r.pot_size}
                          {r.description_source === 'ours' ? ' · our name: Bunnings has not named it on a purchase order yet' : ''}
                        </span>
                      </span>
                      <span className="flex-shrink-0 text-right text-neutral-700">{r.free.toLocaleString()} free</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {excluded.length > 0 && (
              <section aria-label="Left out" className="space-y-2">
                <h2 className="text-sm font-semibold text-neutral-800">Left out</h2>
                <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
                  {excluded.map((r) => (
                    <li key={`${r.kind}-${r.id}`} className="px-4 py-2 flex items-start gap-3 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="block text-neutral-900 break-words">{r.name}</span>
                        <span className="block text-xs text-neutral-500 break-words">
                          {[r.tree_type_name, r.barcode].filter(Boolean).join(' · ') || 'No tree type or barcode'}
                        </span>
                        <span className="mt-1 flex flex-wrap gap-1">
                          {r.reasons.map((reason) => (
                            <span key={reason} className={cn('rounded-full text-xs font-medium px-2 py-0.5',
                              reason === 'below_minimum' ? 'bg-neutral-100 text-neutral-700' : 'bg-amber-100 text-amber-800')}>
                              {REASONS[reason]}
                            </span>
                          ))}
                        </span>
                      </span>
                      <span className="flex-shrink-0 text-right text-neutral-700">{r.free.toLocaleString()} free</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <p className="text-xs text-neutral-500">
              Upload the file in the Bunnings app as it is downloaded. Its first twelve lines are Bunnings&rsquo; own template
              and must stay. A barcode is added on the product or stock item, or by matching it on an imported purchase order.
            </p>
          </>
        )}
      </div>
    </>
  );
}

export default RetailerRange;
